import * as Yup from 'yup';
import { yesNoOther, requiredWhenValueIn, requiredWhenIs } from '../utils';

const ACTIONS_TAKEN_MAX_LENGTH = 2000;
const NOTIFIED_PARTY_MAX_LENGTH = 1000;
// Dataverse attributes behind the name/phone/etc. fields are limited to 100 characters (DY365-20421)
const NOTIFIED_PARTY_FIELD_MAX_LENGTH = 100;

export const notifiedPartySchema = Yup.lazy((value: { partyType?: string }) => {
    const withDate = {
        partyType: Yup.string().required('error_required_field'),
        notifiedDate: Yup.string().required('error_required_field'),
    };

    switch (value?.partyType) {
        case 'potentialVictim':
            return Yup.object({
                ...withDate,
                victimName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                victimPhone: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                notes: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'police':
            return Yup.object({
                ...withDate,
                officerName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                policePhone: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                badgeNumber: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                conversationDetails: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'childWelfare':
            return Yup.object({
                ...withDate,
                agencyName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                representativeName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                referenceNumber: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                conversationDetails: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'emergencyDepartment':
            return Yup.object({
                ...withDate,
                departmentName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                notes: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'otherAgency':
            return Yup.object({
                partyType: Yup.string().required('error_required_field'),
                agencyName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                notes: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'family':
            return Yup.object({
                ...withDate,
                memberName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                relationshipToClient: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                familyPhone: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                conversationDetails: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'workplace':
            return Yup.object({
                ...withDate,
                contactName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                jobTitle: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                workplacePhone: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                conversationDetails: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'learningInstitution':
            return Yup.object({
                ...withDate,
                contactName: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                institutionDepartment: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                institutionPhone: Yup.string().max(NOTIFIED_PARTY_FIELD_MAX_LENGTH).required('error_required_field'),
                conversationDetails: Yup.string().max(NOTIFIED_PARTY_MAX_LENGTH).required('error_required_field'),
            });
        case 'none':
            return Yup.object({
                partyType: Yup.string().required('error_required_field'),
            });
        case 'clinicalSupervisor':
            return Yup.object({
                partyType: Yup.string().required('error_required_field'),
                wereOthersInformed: Yup.boolean().nullable(),
                notifiedDate: Yup.string().when('wereOthersInformed', requiredWhenIs(true)),
            });
        default:
            return Yup.object({
                partyType: Yup.string().required('error_required_field'),
            });
    }
});

export const initialValues = {
    wereThereAnyAtRiskIndividuals: null,
    noOfParticipants: 0,
    participants: [],
};

// Shared with the AU risk assessment embedded in IndividualSupportSession (TPH-1907/1908) —
// everything except personal info (name/dob/phone/email), which only the standalone
// (non-AU) participant has.
export const riskAssessmentCoreSchema = Yup.object({
    riskOfSuicide: Yup.boolean().nullable(),
    suicideRiskFactors: Yup.array().of(Yup.string()),
    riskOfSelfHarm: Yup.boolean().nullable(),
    selfHarmRiskFactors: Yup.array().of(Yup.string()),
    riskOfHarmToFromOthers: Yup.boolean().nullable(),
    harmToFromOthersRiskFactors: Yup.array().of(Yup.string()),
    riskDescription: Yup.string().max(2000),
    riskFactors: Yup.array().of(Yup.string()),
    hasDutyToReport: yesNoOther.notRequired(),
    dutyToReportDescription: Yup.string().max(ACTIONS_TAKEN_MAX_LENGTH).when('hasDutyToReport', requiredWhenValueIn(['Y', 'O'], false)),
    notifiedPartiesDetails: Yup.string().max(ACTIONS_TAKEN_MAX_LENGTH).when('hasDutyToReport', requiredWhenValueIn(['Y', 'O'], false)),
    measureTakenToReduceRisks: Yup.string().max(ACTIONS_TAKEN_MAX_LENGTH),
    agreementAgainstSuicide: yesNoOther.notRequired(),
    agreementAgainstSuicideDescription: Yup.string().max(ACTIONS_TAKEN_MAX_LENGTH).when('agreementAgainstSuicide', requiredWhenValueIn(['Y', 'O'], false)),
    selectedNotifiedParties: Yup.array().of(Yup.string()),
    partiesNotified: Yup.array().of(notifiedPartySchema),
});

const participantSchema = Yup.object({
    name: Yup.string().max(250).optional(),
    dateOfBirth: Yup.string().nullable(),
    phoneNumber: Yup.string().max(100).optional(),
    emailAddress: Yup.string().max(100).optional(),
}).concat(riskAssessmentCoreSchema);

export const validationSchema = Yup.object({
    wereThereAnyAtRiskIndividuals: Yup.boolean().required('error_required_field'),
    noOfParticipants: Yup.number().min(0).required('error_required_field'),
    participants: Yup.array().of(participantSchema),
});
